import React, { createContext, useCallback, useContext, useState } from 'react';
import en from './locales/en.json';
import fil from './locales/fil.json';

// Per-page locale fragments live in ./locales/<lang>/<namespace>.json and are
// merged over the base file, keyed by filename. This lets each page own its own
// translation file (e.g. locales/en/newReferral.json -> t('newReferral.*'))
// without everyone editing one giant en.json/fil.json.
const enFrags = import.meta.glob('./locales/en/*.json', { eager: true, import: 'default' });
const filFrags = import.meta.glob('./locales/fil/*.json', { eager: true, import: 'default' });

function mergeFragments(base, frags) {
  const out = { ...base };
  for (const path of Object.keys(frags)) {
    const ns = path.split('/').pop().replace('.json', '');
    out[ns] = { ...(out[ns] || {}), ...frags[path] };
  }
  return out;
}

const TRANSLATIONS = {
  en: mergeFragments(en, enFrags),
  fil: mergeFragments(fil, filFrags),
};
const LANG_KEY = 'afc_lang';

function resolve(obj, keyPath) {
  return keyPath.split('.').reduce((acc, k) => acc?.[k], obj) ?? keyPath;
}

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem(LANG_KEY);
    return saved === 'fil' ? 'fil' : 'en';
  });

  const changeLang = useCallback((next) => {
    setLang(next);
    localStorage.setItem(LANG_KEY, next);
    document.documentElement.lang = next;
  }, []);

  const t = useCallback(
    (key, vars) => {
      let str = resolve(TRANSLATIONS[lang], key);
      if (vars) {
        Object.entries(vars).forEach(([k, v]) => {
          str = str.replace(`{{${k}}}`, v);
        });
      }
      return str;
    },
    [lang]
  );

  return (
    <LanguageContext.Provider value={{ lang, changeLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTranslation() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useTranslation used outside LanguageProvider');
  return ctx;
}
